import { RefObject, useLayoutEffect } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Focus management for a modal dialog rendered in a portal. While `isOpen`:
 * focus moves into `container` (to `initial`, else its first focusable
 * control, else the container itself — give it tabIndex={-1}); Tab and
 * Shift+Tab cycle inside it; on close, focus returns to the element that had
 * it when the dialog opened, if it is still in the document.
 *
 * A layout effect, so the opener is captured before any child effect moves
 * focus itself — such a child keeps its choice, since focus already inside
 * the container is left alone.
 */
export default function useModalFocus(
  isOpen: boolean,
  container: RefObject<HTMLElement>,
  initial?: RefObject<HTMLElement>,
): void {
  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const opener = document.activeElement as HTMLElement | null;
    const focusables = (): HTMLElement[] =>
      Array.from(
        container.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      );
    const inside = () =>
      Boolean(
        container.current &&
          container.current.contains(document.activeElement),
      );

    if (!inside()) {
      const target =
        (initial && initial.current) || focusables()[0] || container.current;
      if (target) {
        target.focus();
      }
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !container.current) {
        return;
      }
      const list = focusables();
      if (list.length === 0) {
        event.preventDefault();
        container.current.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !inside()) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !inside()) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (opener && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [isOpen]);
}
